import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnifiedSubscription } from '../../contexts/SubscriptionContext';
import { Button } from '../ui/button';
import { LogOut, User, Bell, Sparkles, AlertTriangle, XCircle, Clock } from 'lucide-react';

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { subscription, isPastDue, isCanceled, isCanceledAtPeriodEnd, isAlertBannerVisible } = useUnifiedSubscription();

  // Get subscription status display info
  const getSubscriptionStatusDisplay = () => {
    if (isPastDue) {
      return {
        icon: <AlertTriangle className="h-3 w-3" />,
        label: 'Payment Issue',
        className: 'bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700',
        showPlanName: false
      };
    }
    if (isCanceled) {
      return {
        icon: <XCircle className="h-3 w-3" />,
        label: 'Canceled',
        className: 'bg-gray-100 dark:bg-gray-950/50 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700',
        showPlanName: false
      };
    }
    if (isCanceledAtPeriodEnd) {
      return {
        icon: <Clock className="h-3 w-3" />,
        label: `${subscription?.plan_name} (Canceling)`,
        className: 'bg-orange-100 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300 border border-orange-300 dark:border-orange-700',
        showPlanName: true
      };
    }
    // Normal active subscription
    const planName = subscription?.plan_name?.toLowerCase();
    return {
      icon: <Sparkles className="h-2.5 w-2.5" />,
      label: subscription?.plan_name,
      className: planName === 'bronze'
        ? 'bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300'
        : planName === 'silver'
        ? 'bg-slate-100 dark:bg-slate-950/50 text-slate-700 dark:text-slate-300'
        : planName === 'gold'
        ? 'bg-yellow-100 dark:bg-yellow-950/50 text-yellow-700 dark:text-yellow-300'
        : 'bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300',
      showPlanName: true
    };
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className={`bg-card/95 backdrop-blur-lg border-b border-border sticky z-40 shadow-sm ${isAlertBannerVisible ? 'top-14' : 'top-0'}`}>
      <div className="px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Logo */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-xl bg-gradient-primary bg-clip-text text-transparent">
              TinyScale Studio
            </span>
          </div>
          
          {/* Right side - User actions */}
          <div className="flex items-center space-x-4">
            {user && (
              <>
                {/* Notifications */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-accent"
                >
                  <Bell className="h-4 w-4" />
                </Button>

                {/* User Menu */}
                <div className="flex items-center gap-3">
                  {/* Mobile-only subscription status indicator (visible on xs, hidden on sm+) */}
                  {subscription && (isPastDue || isCanceled || isCanceledAtPeriodEnd) && (
                    <Link
                      to="/billing"
                      className="sm:hidden flex items-center justify-center w-8 h-8 rounded-full animate-pulse"
                      title="Subscription issue - tap to resolve"
                    >
                      {isPastDue ? (
                        <div className="w-8 h-8 bg-red-100 dark:bg-red-950/50 rounded-full flex items-center justify-center border border-red-300 dark:border-red-700">
                          <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </div>
                      ) : isCanceled ? (
                        <div className="w-8 h-8 bg-gray-100 dark:bg-gray-950/50 rounded-full flex items-center justify-center border border-gray-300 dark:border-gray-700">
                          <XCircle className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-orange-100 dark:bg-orange-950/50 rounded-full flex items-center justify-center border border-orange-300 dark:border-orange-700">
                          <Clock className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                        </div>
                      )}
                    </Link>
                  )}

                  {/* Desktop subscription status (hidden on xs, visible on sm+) */}
                  <div className="hidden sm:block text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="text-sm font-medium text-foreground">
                        {user.email?.split('@')[0] || 'User'}
                      </div>
                      {subscription && (() => {
                        const statusDisplay = getSubscriptionStatusDisplay();
                        const hasIssue = isPastDue || isCanceled || isCanceledAtPeriodEnd;

                        return (
                          <Link
                            to="/billing"
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium transition-all hover:opacity-80 ${statusDisplay.className} ${hasIssue ? 'animate-pulse' : ''}`}
                            title={hasIssue ? 'Click to resolve subscription issue' : 'View billing'}
                          >
                            {statusDisplay.icon}
                            {statusDisplay.label}
                          </Link>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>

                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleLogout}
                    className="h-9 w-9 p-0 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};